import React, { useState, useCallback } from 'react';
import { Card, Table, Container, TableBody, TableContainer, Typography } from '@mui/material';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
import { LoadingScreen } from 'src/components/loading-screen';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { useSettingsContext } from 'src/components/settings';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';
import Scrollbar from 'src/components/scrollbar';
import DarkModeButton from 'src/components/button/button-dark-model';
import Iconify from 'src/components/iconify';
import { RouterLink } from 'src/routes/components';
import { useAccount } from '../hooks/use-account';
import AccountTableRow from '../account-table-row';
import AccountTableToolbar from '../account-table-toolbar';
import AccountTableFiltersResult from '../account-table-filters-result';

const TABLE_HEAD = [
  { id: 'tenTaiKhoan', label: 'Tên tài khoản' },
  { id: 'email', label: 'Email' },
  { id: 'sdt', label: 'Số điện thoại', width: 160 },
  { id: 'trangThai', label: 'Trạng thái', width: 140 },
  { id: '', width: 88 },
];

export default function AccountListView() {
  const table = useTable();
  const settings = useSettingsContext();
  const { accounts, loading, error, statusOptions } = useAccount();
  const [filters, setFilters] = useState({ name: '' });
  const router = useRouter(); // Hook để điều hướng trang
  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.account.edit(id));
    },
    [router]
  );

  const dataFiltered = accounts.filter((row) => {
    if (filters.name) {
      const query = filters.name.toLowerCase();
      return (
        row.tenTaiKhoan?.toLowerCase().includes(query) || row.email?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleFilters = useCallback(
    (name: string, value: any) => {
      table.onResetPage();
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [table]
  );

  if (loading) return <LoadingScreen />;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Danh sách tài khoản"
        links={[
          { name: 'Dashboard', href: paths.dashboard.root },
          { name: 'Tài khoản', href: paths.dashboard.account.list },
          { name: 'Danh sách tài khoản' },
        ]}
        sx={{ mb: { xs: 3, md: 5 } }}
        action={
          <DarkModeButton
            component={RouterLink}
            href={paths.dashboard.account.new}
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            Thêm tài khoản
          </DarkModeButton>
        }
      />
      <Card>
        <AccountTableToolbar
          filters={filters}
          onFilters={handleFilters}
          canReset={!!filters.name}
          onResetFilters={() => setFilters({ name: '' })}
        />
        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
              <TableHeadCustom headLabel={TABLE_HEAD} rowCount={dataFiltered.length} />
              <TableBody>
                {dataFiltered
                  .slice(
                    table.page * table.rowsPerPage,
                    table.page * table.rowsPerPage + table.rowsPerPage
                  )
                  .map((row) => (
                    <AccountTableRow
                      key={row.id}
                      row={row}
                      selected={table.selected.includes(row.id!)}
                      onEditRow={() => handleEditRow(row.id!)}
                      statusOptions={statusOptions}
                    />
                  ))}
                <TableEmptyRows
                  height={table.dense ? 52 : 72}
                  emptyRows={emptyRows(table.page, table.rowsPerPage, dataFiltered.length)}
                />
                <TableNoData content="Không có dữ liệu" notFound={dataFiltered.length === 0} />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>
        <TablePaginationCustom
          count={dataFiltered.length}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
        />
      </Card>
    </Container>
  );
}
